import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/Login';
import { WelcomePage } from './pages/Welcome';
import { PlanPicker } from './pages/PlanPicker';
import { OnboardingPage, ONBOARDING_DISMISSED_KEY } from './pages/Onboarding';
import { UpgradePage } from './pages/Upgrade';
import { ErrorPage } from './pages/ErrorPage';
import { UpgradeModal } from './components/UpgradeModal';
import { Sidebar, type View } from './components/Sidebar';
import { ViewToolbar } from './components/ViewToolbar';
import { IdeasView } from './components/IdeasView';
import { DetailPanel } from './components/DetailPanel';
import { QuickCapture } from './components/QuickCapture';
import { ReviewView } from './components/ReviewView';
import { groupDuplicates, reviewCounts } from './utils/duplicate-groups';
import { TrialBanner } from './components/TrialBanner';
import { hasSeenTrialEnded, markTrialEndedSeen, trialJustEnded } from './utils/trial';
import { FeatureGate } from './components/FeatureGate';
import { FilterProvider, useFilters } from './contexts/FilterContext';
import { consumeSharePrefill, hasSharePrefill, type SharePrefill } from './share';
import { useTheme } from './hooks/useTheme';
import * as api from './api';
import type { User, Idea, Tag, DuplicateDetection, ReviewQueue } from './api';
import { Icon } from './icons';
import { reloadOnChunkError } from './utils/lazy-view';
import { NeuralIcon } from './components/agent/NeuralIcon';

// Views off the default /app path load on first use, keeping d3, three, dnd-kit and the
// admin/settings screens out of the initial bundle (P3-14).
const DuplicatesView = lazy(reloadOnChunkError(() => import('./components/DuplicatesView').then((m) => ({ default: m.DuplicatesView }))));
const KanbanBoard = lazy(reloadOnChunkError(() => import('./components/KanbanBoard').then((m) => ({ default: m.KanbanBoard }))));
const SettingsView = lazy(reloadOnChunkError(() => import('./components/SettingsView').then((m) => ({ default: m.SettingsView }))));
const TimelineView = lazy(reloadOnChunkError(() => import('./components/timeline/TimelineView').then((m) => ({ default: m.TimelineView }))));
const MindMapView = lazy(reloadOnChunkError(() => import('./components/MindMapView').then((m) => ({ default: m.MindMapView }))));
const AgentView = lazy(reloadOnChunkError(() => import('./components/AgentView').then((m) => ({ default: m.AgentView }))));
const ArchiveView = lazy(reloadOnChunkError(() => import('./components/ArchiveView').then((m) => ({ default: m.ArchiveView }))));
const AdminView = lazy(reloadOnChunkError(() => import('./components/AdminView').then((m) => ({ default: m.AdminView }))));

/** Board, Map and Review with an empty library (first-run spec §7). */
function EmptyLibrary() {
  return (
    <div className="empty-state empty-state-compact">
      <p>
        Nothing here until you capture an idea. <a href="/app/start">Connect a channel</a>
      </p>
    </div>
  );
}

function AppContent() {
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  // The trial-ended modal shows once per browser (P1-54); this hides it at once on dismiss.
  const [trialEndedDismissed, setTrialEndedDismissed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  // Agent view is parked behind a flag (P1-14); null until the flags load.
  const [agentView, setAgentView] = useState<boolean | null>(null);

  // Navigation — restore view from URL path
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('nr-sidebar-collapsed') === '1'; } catch { return false; }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      try { localStorage.setItem('nr-sidebar-collapsed', !prev ? '1' : '0'); } catch {}
      return !prev;
    });
  }, []);

  const [view, setViewRaw] = useState<View>(() => {
    const path = window.location.pathname;
    if (path === '/board') return 'board';
    if (path === '/map') return 'map';
    if (path === '/timeline') return 'timeline';
    if (path === '/agent') return 'agent';
    if (path === '/duplicates') return 'duplicates';
    if (path === '/review') return 'review';
    if (path === '/archive') return 'archive';
    if (path === '/settings') return 'settings';
    return 'ideas';
  });
  const setView = (v: View) => {
    setViewRaw(v);
    const target = v === 'ideas' ? '/app' : `/${v}`;
    if (window.location.pathname !== target) history.pushState(null, '', target);
  };

  // /agent without the agent_view flag lands on the ideas list instead.
  useEffect(() => {
    if (agentView === false && view === 'agent') {
      setViewRaw('ideas');
      history.replaceState(null, '', '/app');
    }
  }, [agentView, view]);

  // Data
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateDetection[]>([]);
  const [reviewQueue, setReviewQueue] = useState<ReviewQueue | null>(null);
  // Review items acted on this session; shared with the sidebar badge (reviewCounts).
  const [reviewDone, setReviewDone] = useState<ReadonlySet<string>>(() => new Set());
  const [loadingReview, setLoadingReview] = useState(false);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [ideasError, setIdeasError] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<string | null>(null);
  const [semanticLimitMessage, setSemanticLimitMessage] = useState<string | null>(null);
  // Bumped on every fetchIdeas so the board reloads its own per-status columns.
  const [boardReloadKey, setBoardReloadKey] = useState(0);

  // Selection & modals
  const [selectedIdeaId, setSelectedIdeaId] = useState<number | null>(null);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [detailExpanded, setDetailExpanded] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [capturePrefill, setCapturePrefill] = useState<SharePrefill | null>(null);
  const { resolvedTheme } = useTheme();
  const [showUpgradeModal, setShowUpgradeModal] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('upgraded') === '1' || params.get('trial') === '1';
  });
  const isTrialModal = () => new URLSearchParams(window.location.search).get('trial') === '1';

  // Global filters
  const filters = useFilters();

  // Check auth on mount
  useEffect(() => {
    api.getMe()
      .then((me) => {
        if (me.legal?.acceptance_required) {
          api.goToLegalAccept();
          return;
        }
        routeFirstRun(me);
        setUser(me);
        setAuthChecked(true);
        api.getFlags()
          .then(({ flags }) => setAgentView(flags.agent_view))
          .catch(() => setAgentView(false));
      })
      .catch(() => {
        setUser(null);
        setAuthChecked(true);
      });
  }, []);

  // First-run (/app/start) takes over /app for accounts with no ideas, including
  // the ?trial=1 welcome; ?upgraded=1 and pending share captures keep their flows.
  const routeFirstRun = (me: User) => {
    const path = window.location.pathname;
    if (path === '/app/start') {
      if (me.active_idea_count > 0) history.replaceState(null, '', '/app');
      else try { localStorage.removeItem(ONBOARDING_DISMISSED_KEY); } catch {}
      return;
    }
    if (path !== '/app' || me.active_idea_count > 0) return;
    if (new URLSearchParams(window.location.search).get('upgraded') === '1' || hasSharePrefill()) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(ONBOARDING_DISMISSED_KEY) === '1'; } catch {}
    if (dismissed) return;
    history.replaceState(null, '', '/app/start');
    setShowUpgradeModal(false);
  };

  const exitFirstRun = () => {
    history.replaceState(null, '', '/app');
    setViewRaw('ideas');
    setUser((u) => (u ? { ...u } : u));
    refreshUser();
  };

  // Share target: a payload stashed by interceptShareTarget() (possibly before
  // login) opens the capture modal prefilled once the user is authenticated.
  useEffect(() => {
    if (!user) return;
    const prefill = consumeSharePrefill();
    if (prefill) {
      setCapturePrefill(prefill);
      setShowCapture(true);
    }
  }, [user]);

  // Deep link: /ideas/:id opens idea in expanded view
  const [deepLinkId] = useState<number | null>(() => {
    const match = window.location.pathname.match(/^\/ideas\/(\d+)$/);
    return match ? Number(match[1]) : null;
  });

  useEffect(() => {
    if (deepLinkId && user && !loadingIdeas) {
      setSelectedIdeaId(deepLinkId);
      setDetailExpanded(true);
    }
  }, [deepLinkId, user, loadingIdeas]);

  const PAGE_SIZE = 50;

  // Apply client-side filters
  const applyFilters = useCallback((results: Idea[]) => {
    let filtered = results;
    if (filters.selectedTags.length > 0) {
      filtered = filtered.filter((idea) =>
        filters.selectedTags.some((t) => (idea.tags ?? []).includes(t))
      );
    }
    if (filters.selectedStatuses.length > 0) {
      filtered = filtered.filter((idea) =>
        filters.selectedStatuses.includes(idea.status)
      );
    }
    return filtered;
  }, [filters.selectedTags, filters.selectedStatuses]);

  // Fetch ideas — uses global filters
  const fetchIdeas = useCallback(async () => {
    setBoardReloadKey((k) => k + 1);
    setLoadingIdeas(true);
    setIdeasError(null);
    setHasMore(false);
    setSearchType(null);
    setSemanticLimitMessage(null);
    try {
      if (filters.searchQuery.trim()) {
        // Search mode — not paginated (server returns ranked results)
        const data = await api.searchIdeas(filters.searchQuery.trim(), filters.searchMode, {
          ftsMode: filters.searchMode === 'keyword' ? filters.ftsMode : undefined,
        });
        setIdeas(applyFilters(data.results));
        setSearchType(data.search_type);
        if (data.semantic_limit_reached) {
          setSemanticLimitMessage(data.semantic_limit_message ?? null);
        }
      } else {
        // Browse mode — paginated
        const data = await api.listIdeas({
          limit: PAGE_SIZE,
          offset: 0,
          connection_types: filters.selectedConnectionTypes.length > 0
            ? filters.selectedConnectionTypes.join(',')
            : undefined,
        });
        setIdeas(applyFilters(data.ideas));
        setHasMore(data.has_more ?? false);
      }
    } catch (err) {
      setIdeasError((err as Error).message);
    } finally {
      setLoadingIdeas(false);
    }
  }, [filters.searchQuery, filters.searchMode, filters.ftsMode, filters.selectedConnectionTypes, applyFilters]);

  // Load next page of ideas (browse mode only)
  const fetchMoreIdeas = useCallback(async () => {
    if (loadingMore || !hasMore || filters.searchQuery.trim()) return;
    setLoadingMore(true);
    try {
      const data = await api.listIdeas({
        limit: PAGE_SIZE,
        offset: ideas.length,
        connection_types: filters.selectedConnectionTypes.length > 0
          ? filters.selectedConnectionTypes.join(',')
          : undefined,
      });
      setIdeas((prev) => [...prev, ...applyFilters(data.ideas)]);
      setHasMore(data.has_more ?? false);
    } catch {
      // Non-critical — user can retry by scrolling again
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, filters.searchQuery, filters.selectedConnectionTypes, ideas.length, applyFilters]);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    try {
      const data = await api.listTags();
      setTags(data.tags);
    } catch {
      // Tags are non-critical
    }
  }, []);

  // Fetch duplicates
  const fetchDuplicates = useCallback(async () => {
    try {
      const data = await api.listDuplicates();
      setDuplicates(data.duplicates);
    } catch {
      // Non-critical
    }
  }, []);

  // Fetch review queue (also drives the sidebar count)
  const fetchReview = useCallback(async () => {
    setLoadingReview(true);
    try {
      const data = await api.getReviewQueue();
      setReviewQueue(data);
    } catch {
      // Non-critical
    } finally {
      setLoadingReview(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchIdeas();
      fetchTags();
      fetchDuplicates();
      fetchReview();
    }
  }, [user, fetchIdeas, fetchTags, fetchDuplicates, fetchReview]);

  // Fetch idea detail when selected
  useEffect(() => {
    if (selectedIdeaId === null) {
      setSelectedIdea(null);
      setDetailExpanded(false);
      return;
    }
    api.getIdea(selectedIdeaId)
      .then(setSelectedIdea)
      .catch(() => setSelectedIdea(null));
  }, [selectedIdeaId]);

  // Sync URL with expanded idea state
  useEffect(() => {
    const current = window.location.pathname;
    if (detailExpanded && selectedIdeaId) {
      const target = `/ideas/${selectedIdeaId}`;
      if (current !== target) history.pushState(null, '', target);
    } else if (current.startsWith('/ideas/')) {
      history.pushState(null, '', '/app');
    }
  }, [detailExpanded, selectedIdeaId]);

  // Handle browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      const match = path.match(/^\/ideas\/(\d+)$/);
      if (match) {
        setSelectedIdeaId(Number(match[1]));
        setDetailExpanded(true);
      } else {
        setDetailExpanded(false);
      }
      if (path === '/login' || path === '/') return;
      if (path === '/board') setViewRaw('board');
      else if (path === '/map') setViewRaw('map');
      else if (path === '/agent') setViewRaw('agent');
      else if (path === '/duplicates') setViewRaw('duplicates');
      else if (path === '/review') setViewRaw('review');
      else if (path === '/timeline') setViewRaw('timeline');
      else if (path === '/archive') setViewRaw('archive');
      else if (path === '/settings') setViewRaw('settings');
      else if (!match) setViewRaw('ideas');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ⌘K: the one box for search and capture (P3-06, D-78).
  const openSearch = useCallback(() => {
    setShowCapture(false);
    setSearchOpen(true);
  }, []);

  // Global shortcuts: ⌘K / Ctrl+K search, C new idea (not while typing).
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openSearch();
      } else if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        setShowCapture(true);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [openSearch]);

  // Keyboard shortcuts for detail panel
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!selectedIdea) return;

      // Cmd/Ctrl+Shift+P: toggle Edit/Preview in expanded detail panel
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'p') {
        if (detailExpanded) {
          e.preventDefault();
          // Dispatch custom event for DetailPanel to handle
          window.dispatchEvent(new CustomEvent('nrepo:toggle-body-tab'));
        }
        return;
      }

      if (e.key === 'Escape') {
        if (detailExpanded) {
          setDetailExpanded(false);
        } else {
          setSelectedIdeaId(null);
        }
      } else if (e.key === 'e' && !e.ctrlKey && !e.metaKey) {
        // Don't trigger when typing in inputs
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        setDetailExpanded((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selectedIdea, detailExpanded]);

  const handleUpdate = () => {
    fetchIdeas();
    fetchTags();
    fetchDuplicates();
    if (selectedIdeaId) {
      api.getIdea(selectedIdeaId)
        .then(setSelectedIdea)
        .catch(() => setSelectedIdea(null));
    }
  };

  const handleDismissDuplicate = async (dupId: number) => {
    await api.dismissDuplicate(dupId);
    fetchDuplicates();
    fetchReview();
  };

  const handleMergeDuplicate = async (dupId: number) => {
    await api.mergeDuplicate(dupId);
    fetchIdeas();
    fetchDuplicates();
    fetchReview();
    fetchTags();
  };

  const handleSelect = (id: number) => {
    setSelectedIdeaId(id);
    setDetailExpanded(window.innerWidth < 768);
  };

  const handleDoubleClick = (id: number) => {
    setSelectedIdeaId(id);
    setDetailExpanded(true);
  };

  const refreshUser = useCallback(() => {
    api.getMe().then(setUser).catch(() => {});
  }, []);

  // Public pages (no auth required)
  const pathname = window.location.pathname;
  if (pathname === '/error') return <ErrorPage />;
  if (pathname === '/login') return <LoginPage />;

  // Welcome page: unauthenticated → landing, authenticated → redirect to /app
  if (pathname === '/') {
    if (!authChecked) {
      return (
        <div className="loading-screen">
          <NeuralIcon size={48} className="logo-mark-large" />
          <div className="spinner" />
        </div>
      );
    }
    if (user) {
      window.location.replace('/app');
      return null;
    }
    return <WelcomePage />;
  }

  // All other paths require auth
  if (!authChecked) {
    return (
      <div className="loading-screen">
        <NeuralIcon size={48} className="logo-mark-large" />
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // Plan picker for new users
  if (pathname === '/welcome') {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('new') === '1') {
      return <PlanPicker user={user} />;
    }
    window.location.replace('/app');
    return null;
  }

  if (pathname === '/app/start') {
    return <OnboardingPage user={user} onExit={exitFirstRun} />;
  }

  // Upgrade page for existing free users
  if (pathname === '/upgrade') {
    return <UpgradePage user={user} />;
  }

  // Admin dashboard — standalone page, no sidebar
  if (pathname === '/admin') {
    return <Suspense fallback={null}><AdminView /></Suspense>;
  }

  // 404 catch-all for unknown paths
  const knownPaths = ['/app', '/board', '/map', '/timeline', '/agent', '/duplicates', '/review', '/archive', '/settings'];
  if (!knownPaths.includes(pathname) && !pathname.startsWith('/ideas/')) {
    return <ErrorPage />;
  }

  // Both: active_idea_count is fetched once, ideas refresh after a capture
  const libraryEmpty = user.active_idea_count === 0 && ideas.length === 0 && !loadingIdeas;

  // Main app
  return (
    <>
    {user?.is_trialing && user.trial_days_remaining != null && user.trial_days_remaining <= 5 && (
      <TrialBanner daysRemaining={user.trial_days_remaining} ideaCount={user.active_idea_count} />
    )}
    {!trialEndedDismissed && trialJustEnded(user) && !hasSeenTrialEnded(user.id) && (
      <TrialBanner
        daysRemaining={0}
        ideaCount={user.active_idea_count}
        onDismiss={() => { markTrialEndedSeen(user.id); setTrialEndedDismissed(true); }}
      />
    )}
    <div className="app-layout">
      <Sidebar
        currentView={view}
        onViewChange={(v) => { setView(v); setSelectedIdeaId(null); setDetailExpanded(false); }}
        user={user}
        onCapture={() => setShowCapture(true)}
        onSearch={openSearch}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        duplicateCount={groupDuplicates(duplicates).length}
        reviewCount={reviewCounts(reviewQueue, reviewDone).total}
        agentView={agentView === true}
      />

      <main className="main-area">
        {view === 'settings' && (
          <div className="top-bar">
            <h1 className="view-title">Settings</h1>
          </div>
        )}

        {(view === 'ideas' || view === 'board' || view === 'map') && !(libraryEmpty && view !== 'ideas') && (
          <ViewToolbar
            title={view === 'ideas' ? 'Ideas' : view === 'board' ? 'Board' : 'Mind map'}
            count={view === 'ideas' ? user.active_idea_count : undefined}
            tags={tags}
            showLinks={view !== 'map'}
          />
        )}

        <div className="content-area">
          <div className={`content-main${selectedIdea && !detailExpanded ? ' content-main-with-detail' : ''}`}>
            <Suspense fallback={null}>
            {view === 'ideas' && (
              <IdeasView
                ideas={ideas}
                loading={loadingIdeas}
                error={ideasError}
                selectedId={selectedIdeaId}
                onSelect={handleSelect}
                onDoubleClick={handleDoubleClick}
                duplicates={duplicates}
                onDismissDuplicate={handleDismissDuplicate}
                onMergeDuplicate={handleMergeDuplicate}
                hasMore={hasMore}
                loadingMore={loadingMore}
                onLoadMore={fetchMoreIdeas}
                searchType={searchType}
                semanticLimitMessage={semanticLimitMessage}
                user={user}
                onBulkChanged={() => { fetchIdeas(); fetchTags(); fetchDuplicates(); setSelectedIdeaId(null); }}
                onCapture={() => setShowCapture(true)}
              />
            )}

            {libraryEmpty && (view === 'board' || view === 'map' || view === 'review') && <EmptyLibrary />}

            {view === 'board' && !libraryEmpty && (
              <KanbanBoard
                searchResults={filters.searchQuery.trim() ? ideas : null}
                reloadKey={boardReloadKey}
                onSelect={handleSelect}
                onDoubleClick={handleDoubleClick}
                onUpdate={handleUpdate}
              />
            )}

            {view === 'map' && user && !libraryEmpty && (
              <MindMapView
                onSelect={(id) => {
                  if (id === 0) {
                    setSelectedIdeaId(null);
                  } else {
                    handleSelect(id);
                  }
                }}
                onDoubleClick={handleDoubleClick}
                userId={user.id}
                userPlan={user.plan}
                agentView={agentView === true}
              />
            )}

            {view === 'timeline' && <TimelineView onSelect={handleSelect} />}

            {view === 'agent' && agentView && (
              user?.plan === 'free' ? (
                <FeatureGate feature="agent" onBack={() => setView('ideas')} />
              ) : (
                <AgentView user={user} />
              )
            )}

            {view === 'duplicates' && (
              user?.plan === 'free' ? (
                <FeatureGate feature="duplicates" onBack={() => setView('ideas')} />
              ) : (
                <DuplicatesView
                  duplicates={duplicates}
                  onChanged={(merged) => {
                    fetchDuplicates();
                    fetchReview();
                    if (merged) { fetchIdeas(); fetchTags(); }
                  }}
                  onSelect={handleSelect}
                />
              )
            )}

            {view === 'review' && !libraryEmpty && (
              <ReviewView
                queue={reviewQueue}
                loading={loadingReview}
                ideas={ideas}
                onSelect={handleSelect}
                onChanged={() => { fetchReview(); fetchIdeas(); fetchDuplicates(); }}
                done={reviewDone}
                onDone={(keys) => setReviewDone((prev) => new Set([...prev, ...keys]))}
              />
            )}

            {view === 'archive' && (
              <ArchiveView onRestored={() => { fetchIdeas(); fetchTags(); fetchReview(); refreshUser(); }} />
            )}

            {view === 'settings' && (
              <SettingsView user={user} onImported={() => { fetchIdeas(); fetchTags(); fetchReview(); refreshUser(); }} />
            )}
            </Suspense>
          </div>

          {/* Peek panel (side panel) */}
          {selectedIdea && !detailExpanded && (
            <DetailPanel
              idea={selectedIdea}
              tags={tags}
              user={user}
              onClose={() => setSelectedIdeaId(null)}
              onUpdate={handleUpdate}
              onNavigateSettings={() => { setView('settings'); setSelectedIdeaId(null); }}
              onToggleExpand={() => setDetailExpanded(true)}
            />
          )}

          {/* Expanded detail overlay — content stays mounted underneath */}
          {detailExpanded && selectedIdea && (
            <div className="detail-expanded-overlay">
              <DetailPanel
                idea={selectedIdea}
                tags={tags}
                user={user}
                onClose={() => { setSelectedIdeaId(null); setDetailExpanded(false); }}
                onUpdate={handleUpdate}
                onNavigateSettings={() => { setView('settings'); setSelectedIdeaId(null); setDetailExpanded(false); }}
                expanded
                onToggleExpand={() => setDetailExpanded(false)}
              />
            </div>
          )}
        </div>
      </main>

      {(showCapture || searchOpen) && (
        <QuickCapture
          mode={showCapture ? 'capture' : 'search'}
          user={user}
          tags={tags}
          initialTitle={capturePrefill?.title}
          initialBody={capturePrefill?.body}
          onClose={() => { setShowCapture(false); setSearchOpen(false); setCapturePrefill(null); }}
          onCreated={() => { fetchIdeas(); fetchTags(); fetchDuplicates(); refreshUser(); }}
          onOpenIdea={(id) => { if (view !== 'ideas' && view !== 'board' && view !== 'map' && view !== 'timeline') setView('ideas'); handleSelect(id); }}
          onFilterTag={(tag) => { setView('ideas'); if (!filters.selectedTags.includes(tag)) filters.toggleTag(tag); }}
          onGoTo={(v) => { setView(v); setSelectedIdeaId(null); setDetailExpanded(false); }}
        />
      )}

    </div>

    <nav className="mobile-tab-bar">
      <button className={`mobile-tab${view === 'ideas' ? ' mobile-tab-active' : ''}`} onClick={() => { setView('ideas'); }}>
        <span className="mobile-tab-icon"><Icon name="ideas" size={20} /></span>
        <span className="mobile-tab-label">Ideas</span>
      </button>
      <button className={`mobile-tab${searchOpen ? ' mobile-tab-active' : ''}`} onClick={openSearch}>
        <span className="mobile-tab-icon"><Icon name="search" size={20} /></span>
        <span className="mobile-tab-label">Search</span>
      </button>
      <button className={`mobile-tab${view === 'board' ? ' mobile-tab-active' : ''}`} onClick={() => { setView('board'); }}>
        <span className="mobile-tab-icon"><Icon name="board" size={20} /></span>
        <span className="mobile-tab-label">Board</span>
      </button>
      <button className={`mobile-tab${view === 'settings' ? ' mobile-tab-active' : ''}`} onClick={() => { setView('settings'); }}>
        <span className="mobile-tab-icon"><Icon name="settings" size={20} /></span>
        <span className="mobile-tab-label">Settings</span>
      </button>
    </nav>

    {showUpgradeModal && (
      <UpgradeModal
        trial={isTrialModal()}
        trialEndDate={user?.trial_days_remaining != null
          ? new Date(Date.now() + user.trial_days_remaining * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : undefined}
        onDismiss={() => {
          setShowUpgradeModal(false);
          history.replaceState({}, '', '/app');
          refreshUser();
        }}
      />
    )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <FilterProvider>
        <AppContent />
      </FilterProvider>
    </ErrorBoundary>
  );
}

export default App;
